import { FC } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from 'react-router-dom';

const loginSchema = z.object({
  email: z.string().email("Ingresa un correo electrónico válido"),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres"),
});

type LoginForm = z.infer<typeof loginSchema>;

interface LoginFormProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const LoginForm: FC<LoginFormProps> = ({ isLoading, setIsLoading }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true);
      console.log('Attempting login...');
      
      // Clear any existing session and tokens
      localStorage.removeItem('supabase.auth.token');
      await supabase.auth.signOut();
      
      // Attempt to sign in with fresh session
      const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (signInError) {
        console.error('Login error:', signInError);
        
        if (signInError.message?.includes('Invalid login credentials')) {
          toast({
            variant: "destructive",
            title: "Error de inicio de sesión",
            description: "Credenciales incorrectas. Por favor, verifica tus datos.",
          });
          return;
        }
        
        throw signInError;
      }

      if (!authData.session) {
        console.error('No session data received');
        toast({
          variant: "destructive",
          title: "Error de inicio de sesión",
          description: "No se pudo iniciar sesión. Por favor, intenta de nuevo.",
        });
        return;
      }

      // Verify if the user is admin
      let userRole = 'user';
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', authData.session.user.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        
        // Si el perfil no existe, vamos a crearlo
        if (profileError.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert([
              {
                id: authData.session.user.id,
                email: authData.session.user.email,
                role: 'user',
                created_at: new Date().toISOString()
              }
            ]);

          if (insertError) {
            toast({
              variant: "destructive",
              title: "Error",
              description: "No se pudo crear el perfil de usuario.",
            });
            return;
          }
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Error al obtener el perfil de usuario.",
          });
          return;
        }
      } else {
        userRole = profile.role;
      }

      console.log('Login successful, user role:', userRole);
      toast({
        title: "¡Bienvenido!",
        description: "Has iniciado sesión correctamente.",
      });
      
      // Add a small delay before redirect to ensure toast is visible
      setTimeout(() => {
        // Redirect based on role
        if (userRole === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 500);
      
    } catch (error) {
      console.error('Unexpected error during login:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Ocurrió un error inesperado. Por favor, intenta de nuevo.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-5 pb-5">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Correo electrónico</Label>
          <Input
            id="email"
            type="email"
            placeholder="ejemplo@correo.com"
            {...form.register("email")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Contraseña</Label>
          <Input
            id="password"
            type="password"
            placeholder="Tu contraseña"
            {...form.register("password")}
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-primary via-secondary to-accent hover:opacity-90 transition-opacity"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">⚡</span>
              Iniciando sesión...
            </span>
          ) : (
            "Iniciar sesión"
          )}
        </Button>

        <div className="text-center md:text-left space-y-2">
          <p className="text-sm text-gray-600">
            ¿No tienes una cuenta?{" "}
            <Link
              to="/register"
              className="text-primary hover:text-primary/80 font-semibold hover:underline"
            >
              Regístrate
            </Link>
          </p>
        </div>
      </div>
    </form>
  );
};